/**
 * Upload zone component - drop files or click to upload
 * 
 * Validates file types and sizes before upload.
 */

import { useState, useRef } from 'react';

// Cloudflare Images limit is 10MB
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Maximum files per upload batch (prevents browser memory issues and API overload)
const MAX_FILES_PER_UPLOAD = 150;

// Allowed image MIME types
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

interface UploadZoneProps {
  onUpload: (files: File[]) => void;
  disabled?: boolean;
}

interface ValidationResult {
  valid: File[];
  rejected: Array<{ name: string; reason: string }>;
}

/**
 * Validate files for upload
 */
function validateFiles(files: File[]): ValidationResult {
  const valid: File[] = [];
  const rejected: Array<{ name: string; reason: string }> = [];

  for (const file of files) {
    // Check file type
    if (!file.type.startsWith('image/')) {
      rejected.push({ name: file.name, reason: 'Not an image file' });
      continue;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      rejected.push({ name: file.name, reason: `Unsupported format: ${file.type}` });
      continue;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      rejected.push({ 
        name: file.name, 
        reason: `File too large (${sizeMB}MB). Maximum is ${MAX_FILE_SIZE_MB}MB` 
      });
      continue;
    }

    valid.push(file);
  }

  return { valid, rejected };
}

export function UploadZone({ onUpload, disabled = false }: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: File[]) => {
    setValidationError(null);
    
    // Check batch size limit
    if (files.length > MAX_FILES_PER_UPLOAD) {
      setValidationError(
        `Maximum ${MAX_FILES_PER_UPLOAD} files per upload. You selected ${files.length} files. ` +
        `Please upload in smaller batches.`
      );
      setTimeout(() => setValidationError(null), 8000);
      return;
    }
    
    const { valid, rejected } = validateFiles(files);

    // Show error for rejected files
    if (rejected.length > 0) {
      const errorMessage = rejected
        .map(r => `${r.name}: ${r.reason}`)
        .join('\n');
      setValidationError(errorMessage);
      
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setValidationError(null), 5000);
    }

    // Upload valid files
    if (valid.length > 0) {
      onUpload(valid);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (disabled) return;
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  return (
    <div>
      {/* Validation error message */}
      {validationError && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            marginBottom: '12px',
            color: '#991b1b',
            fontSize: '13px',
            whiteSpace: 'pre-line',
          }}
        >
          {validationError}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '150px',
          border: `2px dashed ${isDragging ? '#3b82f6' : '#d1d5db'}`,
          borderRadius: '12px',
          backgroundColor: isDragging ? '#eff6ff' : '#f9fafb',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          padding: '24px',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <div
          style={{
            fontSize: '32px',
            marginBottom: '8px',
          }}
        >
          +
        </div>
        <div
          style={{
            fontSize: '14px',
            color: '#6b7280',
            textAlign: 'center',
          }}
        >
          Drop images here
          <br />
          or click to add
        </div>
        <div
          style={{
            fontSize: '12px',
            color: '#9ca3af',
            marginTop: '8px',
          }}
        >
          Max {MAX_FILE_SIZE_MB}MB per file
        </div>
      </div>
    </div>
  );
}
